import { useQuery } from "@tanstack/react-query"
import { ExternalLink, Loader2, UserCheck, UserX } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { supabase } from "@/lib/supabase"

interface PersonRecord {
  id: string
  person_id: string
  person: string
  first_name: string
  surname: string
  email: string
  phone_number?: string
  status: string
  employer?: string
}

interface PersonRecordSelectorProps {
  currentPersonId?: string | null
}

const PersonRecordSelector = ({ currentPersonId }: PersonRecordSelectorProps) => {
  const { data: currentPerson, isLoading } = useQuery({
    queryKey: ["personRecord", currentPersonId],
    enabled: !!currentPersonId,
    queryFn: async () => {
      const { data: person, error } = await supabase
        .from("people")
        .select("id, person_id, person, first_name, surname, email, phone_number, status, employer")
        .eq("id", currentPersonId)
        .single()

      if (error) return null
      return person as PersonRecord
    },
  })

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      Active: "bg-green-100 text-green-800 border-green-200",
      Inactive:
        "bg-palette-jungleGreen100 text-palette-jungleGreen800 border-palette-jungleGreen200",
      Banned: "bg-red-100 text-red-800 border-red-200",
    }
    return (
      colors[status] ||
      "bg-palette-jungleGreen100 text-palette-jungleGreen800 border-palette-jungleGreen200"
    )
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserCheck className="h-5 w-5" />
            Person Record
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {currentPerson ? (
            <UserCheck className="h-5 w-5 text-green-600" />
          ) : (
            <UserX className="h-5 w-5 text-amber-600" />
          )}
          Person Record
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {currentPerson ? (
          <div className="p-4 border rounded-lg bg-green-50 border-green-200">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <h4 className="font-semibold text-lg">
                    {currentPerson.first_name} {currentPerson.surname}
                  </h4>
                  <Badge className={getStatusColor(currentPerson.status)}>
                    {currentPerson.status}
                  </Badge>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={!currentPerson.person_id}
                  onClick={() =>
                    window.open(`/people?personId=${currentPerson.person_id}`, "_blank")
                  }
                  className="h-8"
                >
                  <ExternalLink className="h-4 w-4 mr-1" />
                  View
                </Button>
              </div>
              <div className="text-sm text-palette-jungleGreen600 space-y-1">
                <p>
                  <span className="font-medium">Email:</span> {currentPerson.email}
                </p>
                {currentPerson.phone_number && (
                  <p>
                    <span className="font-medium">Phone:</span> {currentPerson.phone_number}
                  </p>
                )}
                {currentPerson.employer && (
                  <p>
                    <span className="font-medium">Employer:</span> {currentPerson.employer}
                  </p>
                )}
                <p className="text-xs text-palette-jungleGreen500 mt-2">
                  Person ID: {currentPerson.person_id}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-4 border rounded-lg bg-amber-50 border-amber-200">
            <p className="text-sm text-amber-800">
              No person record is currently linked to this application.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default PersonRecordSelector
